import { Button, Chip } from "@heroui/react";
import HintTooltip from "@/components/common/HintTooltip";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { Trash2 } from "lucide-react";

import EditableChip from "@/components/common/EditableChip";
import EditableTextCell from "@/components/common/EditableTextCell";
import {
  DESIGNATED_VENDOR_LABEL,
  DESIGNATED_VENDOR_OPTIONS,
  type DesignatedVendor,
  type Partner,
} from "./api";

/**
 * 取引先一覧の列定義。**ヘッダとセルの単一ソース**。
 *
 * ngtg_msc の `buildPartnerColumns` に対応する。テキスト項目はクリックで
 * 1 項目だけ編集するセル（[[EditableTextCell]]）にし、編集ダイアログの開閉は
 * 一覧側が持つ（セルごとに Modal を持たせないため）。
 *
 * 列の並び・ヘッダ名は msc の取引先画面に合わせてある。
 */

/** クリックで編集できる項目。 */
export interface PartnerField {
  key: keyof Partner & string;
  label: string;
  size: number;
  required?: boolean;
  /** 入力の種類。既定はテキスト。 */
  kind?: "text" | "select";
  /** kind が "select" のときの選択肢。 */
  options?: { key: string; label: string }[];
  /** 編集ダイアログの補足。 */
  description?: string;
  /** API に渡す並び替えキー。未指定なら並び替え不可。 */
  sortKey?: string;
  /** 常に表示し、非表示にできない列。 */
  mandatory?: boolean;
  /** 左に固定する列。 */
  pinned?: boolean;
}

export const PARTNER_FIELDS: PartnerField[] = [
  {
    key: "partner_code",
    label: "取引先コード",
    size: 150,
    required: true,
    sortKey: "partner_code",
    mandatory: true,
    pinned: true,
  },
  {
    key: "partner_name",
    label: "取引先",
    size: 220,
    required: true,
    sortKey: "partner_name",
  },
  {
    key: "designated_vendor",
    label: "指定業者",
    size: 130,
    kind: "select",
    options: DESIGNATED_VENDOR_OPTIONS,
    description:
      "見積依頼ファイルの取り込みで、どの得意先の依頼かをこの区分から決めます。同じ区分は 1 社にしか設定できません。",
  },
  { key: "partner_kana", label: "カナ", size: 180, sortKey: "partner_kana" },
  { key: "postal_code", label: "郵便番号", size: 120 },
  { key: "address", label: "住所1", size: 240 },
  { key: "sub_address", label: "住所2", size: 240 },
  { key: "phone_number", label: "代表TEL", size: 150 },
  { key: "phone_sub_number", label: "TEL", size: 150 },
  { key: "fax_number", label: "FAX", size: 150 },
  { key: "fax_sub_number", label: "FAX2", size: 150 },
  {
    key: "contact_name",
    label: "担当者名",
    size: 220,
    description:
      "客先の一覧表の表記をそのまま入れてください（「技術部 上田良允様」「送付先：上原様 問合せ先：黒田様」など）。帳票の宛名に使います。",
  },
  { key: "email_address", label: "E-mail", size: 220 },
];

/** 用途のフラグ。得意先と直送先は兼ねられるので、区分ではなく 2 つのフラグ。 */
export type PartnerRole = "is_client" | "is_ship_to";

export const PARTNER_ROLE_LABEL: Record<PartnerRole, string> = {
  is_client: "得意先",
  is_ship_to: "直送先",
};

export interface PartnerColumnHandlers {
  /** 取引先を編集できるか（マスタ編集は ADMIN のみ）。 */
  canWrite: boolean;
  onEditField: (partner: Partner, field: PartnerField) => void;
  onToggleDisabled: (partner: Partner) => void;
  onToggleRole: (partner: Partner, role: PartnerRole) => void;
  onDelete: (partner: Partner) => void;
}

const helper = createColumnHelper<Partner>();

/** ISO 日時を「YYYY-MM-DD」で表示する（一覧では時刻まで要らない）。 */
function formatDate(value: string | null): string {
  return value ? value.slice(0, 10) : "—";
}

export function buildPartnerColumns({
  canWrite,
  onEditField,
  onToggleDisabled,
  onToggleRole,
  onDelete,
}: PartnerColumnHandlers): ColumnDef<Partner, never>[] {
  const fieldColumns = PARTNER_FIELDS.map((field) =>
    helper.accessor((row) => row[field.key], {
      id: field.key,
      header: field.label,
      size: field.size,
      meta: {
        label: field.label,
        mandatory: field.mandatory,
        pinned: field.pinned,
        sortKey: field.sortKey,
      },
      cell: (ctx) => {
        const value = ctx.getValue() as string | null;

        // 選択肢から選ぶ項目は、自由入力のセル（下線付きリンク）ではなく
        // 取引状態と同じ Chip で見せる。下線が付くと手で打てるように見える。
        if (field.kind === "select") {
          const chip = value ? (
            <Chip size="sm" variant="flat" color="primary">
              {DESIGNATED_VENDOR_LABEL[value as DesignatedVendor]}
            </Chip>
          ) : (
            <span className="text-default-400">—</span>
          );
          return (
            <EditableChip
              label={`${field.label}を編集`}
              canWrite={canWrite}
              onPress={() => onEditField(ctx.row.original, field)}
            >
              {chip}
            </EditableChip>
          );
        }

        return (
          <EditableTextCell
            label={field.label}
            value={value}
            canWrite={canWrite}
            onEdit={() => onEditField(ctx.row.original, field)}
          />
        );
      },
    }),
  );

  // 用途。得意先・直送先は同じ表に入っているので、どちらに出る相手かを見せる。
  // 選択肢はこのフラグで絞るため、台帳では一目で分かる位置に置く。
  const roleColumn = helper.display({
    id: "roles",
    header: "用途",
    size: 160,
    meta: { label: "用途" },
    cell: (ctx) => {
      const partner = ctx.row.original;
      return (
        <div className="flex gap-1">
          {(Object.keys(PARTNER_ROLE_LABEL) as PartnerRole[]).map((role) => {
            const on = partner[role];
            const chip = (
              <Chip
                size="sm"
                variant={on ? "flat" : "bordered"}
                color={on ? "primary" : "default"}
                className={on ? "" : "text-default-400"}
              >
                {PARTNER_ROLE_LABEL[role]}
              </Chip>
            );
            if (!canWrite) return <span key={role}>{chip}</span>;
            return (
              <button
                key={role}
                type="button"
                className="cursor-pointer"
                onClick={() => onToggleRole(partner, role)}
                aria-label={`${PARTNER_ROLE_LABEL[role]}として使う設定を${on ? "外す" : "入れる"}`}
              >
                {chip}
              </button>
            );
          })}
        </div>
      );
    },
  });

  // 取引停止（kyowa 固有）。取引先は削除せずここで候補から外す運用にする。
  const disabledColumn = helper.accessor("disabled", {
    id: "disabled",
    header: "取引",
    size: 110,
    meta: { label: "取引状態" },
    cell: (ctx) => {
      const stopped = ctx.getValue();
      const chip = (
        <Chip size="sm" variant="flat" color={stopped ? "danger" : "success"}>
          {stopped ? "取引停止" : "取引中"}
        </Chip>
      );
      if (!canWrite) return chip;
      return (
        <button
          type="button"
          className="cursor-pointer"
          onClick={() => onToggleDisabled(ctx.row.original)}
          aria-label={stopped ? "取引を再開する" : "取引を停止する"}
        >
          {chip}
        </button>
      );
    },
  });

  const timestampColumns = [
    helper.accessor("created_at", {
      id: "created_at",
      header: "作成日",
      size: 120,
      meta: { label: "作成日", sortKey: "created_at" },
      cell: (ctx) => formatDate(ctx.getValue()),
    }),
    helper.accessor("updated_at", {
      id: "updated_at",
      header: "更新日",
      size: 120,
      meta: { label: "更新日" },
      cell: (ctx) => formatDate(ctx.getValue()),
    }),
  ];

  const actionsColumn = helper.display({
    id: "actions",
    header: "操作",
    size: 80,
    enableResizing: false,
    meta: { label: "操作", mandatory: true },
    cell: (ctx) => (
      <HintTooltip content={canWrite ? "削除" : "管理者の権限が必要です"}>
        {/* 無効なボタンはイベントを出さないため、Tooltip 用に包む。 */}
        <span className="inline-flex">
          <Button
            isIconOnly
            size="sm"
            variant="light"
            color="danger"
            aria-label={`${ctx.row.original.partner_name} を削除`}
            isDisabled={!canWrite}
            onPress={() => onDelete(ctx.row.original)}
          >
            <Trash2 size={16} />
          </Button>
        </span>
      </HintTooltip>
    ),
  });

  // 取引状態は普段そう見ない項目なので、操作列の直前（右端）に置く。
  return [
    ...fieldColumns,
    ...timestampColumns,
    roleColumn,
    disabledColumn,
    actionsColumn,
  ] as ColumnDef<Partner, never>[];
}

/** 列 id の一覧（設定の永続化で「未知のキー」を捨てるのに使う）。表示順と一致させる。 */
export const partnerColumnIds = [
  ...PARTNER_FIELDS.map((f) => f.key),
  "created_at",
  "updated_at",
  "roles",
  "disabled",
  "actions",
];

/** 左に固定する列の id。 */
export const partnerPinnedColumnIds = PARTNER_FIELDS.filter((f) => f.pinned).map(
  (f) => f.key,
);
